/**
 * Live Driver Map Component
 * Real-time driver positions on map, plus the selected driver's path today and next stops
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { DriverWithPosition, Route, RouteStop, TrackPoint } from '../../types';
import { setMapLanguageToArabic, ARABIC_MAP_STYLE } from '../../utils/mapbox';
import { isValidCoordinate } from '../../utils/coordinates';
import { formatTimeBaghdad } from '../../utils/date';
import { driverStops, isDone, pathLines } from './driverPath';

interface LiveDriverMapProps {
  drivers: DriverWithPosition[];
  selectedDriverUid?: string;
  onDriverSelect?: (driverUid: string) => void;
  onClearSelection?: () => void;
  /** Selected driver's GPS points today, oldest first (undefined while loading) */
  trail?: TrackPoint[];
  /** Selected driver's routes today, with stops (undefined while loading) */
  routes?: Array<Route & { stops?: RouteStop[] }>;
  /** True when the trail / routes query failed (stops the loading text and unblocks the camera fit) */
  trailError?: boolean;
  routesError?: boolean;
  className?: string;
}

// Default center (Baghdad)
const DEFAULT_CENTER: [number, number] = [44.366, 33.315];
const DEFAULT_ZOOM = 11;

// Status colors based on position age
const STATUS_LEGEND = [
  { label: 'Active (< 5 min)', color: '#10B981' },
  { label: 'Recent (< 15 min)', color: '#F59E0B' },
  { label: 'Stale (< 60 min)', color: '#9CA3AF' },
  { label: 'Offline', color: '#EF4444' },
];

// Map paint needs hex; the legend uses the matching Tailwind classes (bg-blue-600, border-gray-700)
const PATH_COLOR = '#2563EB'; // blue-600
const NEXT_COLOR = '#374151'; // gray-700
const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

// Get color based on position age
const getStatusColor = (recordedAt: string): string => {
  const ageMinutes = (Date.now() - new Date(recordedAt).getTime()) / 60000;
  if (ageMinutes < 5) return '#10B981';
  if (ageMinutes < 15) return '#F59E0B';
  if (ageMinutes < 60) return '#9CA3AF';
  return '#EF4444';
};

// Ease a marker to its new point instead of jumping; instant when the OS asks for reduced motion.
const glide = (marker: mapboxgl.Marker, to: [number, number], raf: Map<string, number>, uid: string, ms = 1500) => {
  cancelAnimationFrame(raf.get(uid) ?? 0);
  raf.delete(uid);
  const from = marker.getLngLat();
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || (from.lng === to[0] && from.lat === to[1])) {
    marker.setLngLat(to);
    return;
  }
  const t0 = performance.now();
  const step = (t: number) => {
    const k = Math.min((t - t0) / ms, 1);
    marker.setLngLat([from.lng + (to[0] - from.lng) * k, from.lat + (to[1] - from.lat) * k]);
    if (k < 1) raf.set(uid, requestAnimationFrame(step));
    else raf.delete(uid);
  };
  raf.set(uid, requestAnimationFrame(step));
};

const setSource = (map: mapboxgl.Map, id: string, data: GeoJSON.FeatureCollection) =>
  (map.getSource(id) as mapboxgl.GeoJSONSource | undefined)?.setData(data);

export default function LiveDriverMap({
  drivers,
  selectedDriverUid,
  onDriverSelect,
  onClearSelection,
  trail,
  routes,
  trailError,
  routesError,
  className,
}: LiveDriverMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<Map<string, mapboxgl.Marker>>(new Map());
  const glideRaf = useRef(new Map<string, number>());
  const [mapLoaded, setMapLoaded] = useState(false);
  const fittedKey = useRef('');
  const selectedFitKey = useRef('');

  const selectedDriver = drivers.find((d) => d.driver_uid === selectedDriverUid);
  const stops = useMemo(() => (routes ? driverStops(routes) : []), [routes]);
  const openStops = useMemo(() => stops.filter((s) => !isDone(s)), [stops]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: ARABIC_MAP_STYLE,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Set Arabic language for map labels
    setMapLanguageToArabic(map.current);

    map.current.on('load', () => {
      const m = map.current!;
      // Selected driver's layers, drawn under the DOM driver markers
      for (const id of ['sel-path', 'sel-next', 'sel-stops']) m.addSource(id, { type: 'geojson', data: EMPTY });
      m.addLayer({
        id: 'sel-path',
        type: 'line',
        source: 'sel-path',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': PATH_COLOR, 'line-width': 4, 'line-opacity': 0.85 },
      });
      m.addLayer({
        id: 'sel-next',
        type: 'line',
        source: 'sel-next',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': NEXT_COLOR, 'line-width': 2.5, 'line-dasharray': [2, 2] },
      });
      const done: mapboxgl.Expression = ['boolean', ['get', 'done'], false];
      m.addLayer({
        id: 'sel-stops',
        type: 'circle',
        source: 'sel-stops',
        paint: {
          'circle-radius': ['case', done, 5, 11],
          'circle-color': ['match', ['get', 'status'], 'failed', '#EF4444', 'completed', '#9CA3AF', 'skipped', '#9CA3AF', '#FFFFFF'],
          'circle-stroke-color': ['case', done, '#FFFFFF', NEXT_COLOR],
          'circle-stroke-width': 2,
        },
      });
      m.addLayer({
        id: 'sel-stops-label',
        type: 'symbol',
        source: 'sel-stops',
        filter: ['!', done],
        layout: { 'text-field': ['get', 'label'], 'text-size': 11, 'text-allow-overlap': true, 'text-ignore-placement': true },
        paint: { 'text-color': '#111827' },
      });
      setMapLoaded(true);
    });

    const raf = glideRaf.current;
    return () => {
      raf.forEach((id) => cancelAnimationFrame(id));
      raf.clear();
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Update driver markers
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Filter drivers with valid coordinates
    const validDrivers = drivers.filter(
      (d) => d.lat != null && d.lng != null && isValidCoordinate(d.lat, d.lng)
    );

    const currentDriverUids = new Set(validDrivers.map((d) => d.driver_uid));

    // Remove markers for drivers no longer in list
    markers.current.forEach((marker, uid) => {
      if (!currentDriverUids.has(uid)) {
        cancelAnimationFrame(glideRaf.current.get(uid) ?? 0);
        glideRaf.current.delete(uid);
        marker.remove();
        markers.current.delete(uid);
      }
    });

    // Update or create markers for each driver
    validDrivers.forEach((driver) => {
      const isSelected = driver.driver_uid === selectedDriverUid;
      const color = getStatusColor(driver.recorded_at);

      let marker = markers.current.get(driver.driver_uid);

      if (marker) {
        glide(marker, [driver.lng, driver.lat], glideRaf.current, driver.driver_uid);

        // Mapbox owns the root element's transform (position); style the inner dot
        const dot = marker.getElement().firstElementChild as HTMLElement;
        dot.dataset.selected = isSelected ? '1' : '';
        dot.style.borderColor = isSelected ? '#1D4ED8' : 'white';
        dot.style.transform = isSelected ? 'scale(1.2)' : 'scale(1)';
        dot.style.backgroundColor = color;
        const arrow = dot.firstElementChild as HTMLElement | null;
        if (arrow && driver.heading !== undefined) arrow.style.transform = `rotate(${driver.heading}deg)`;
      } else {
        // Create new marker
        const el = document.createElement('div');
        el.className = 'live-driver-marker';
        const dot = document.createElement('div');
        dot.style.cssText = `
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: ${color};
          border: 3px solid ${isSelected ? '#1D4ED8' : 'white'};
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transform: ${isSelected ? 'scale(1.2)' : 'scale(1)'};
          transition: transform 0.2s ease, background-color 0.3s ease, border-color 0.3s ease;
        `;
        dot.dataset.selected = isSelected ? '1' : '';
        el.appendChild(dot);

        // Add direction indicator if moving
        if (driver.heading !== undefined) {
          const arrow = document.createElement('div');
          arrow.style.cssText = `
            width: 0;
            height: 0;
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-bottom: 10px solid white;
            transform: rotate(${driver.heading}deg);
          `;
          dot.appendChild(arrow);
        }

        // Click handler
        el.addEventListener('click', () => {
          onDriverSelect?.(driver.driver_uid);
        });

        // Hover effects
        el.addEventListener('mouseenter', () => {
          dot.style.transform = 'scale(1.2)';
        });
        el.addEventListener('mouseleave', () => {
          if (!dot.dataset.selected) {
            dot.style.transform = 'scale(1)';
          }
        });

        // Create popup
        const popup = new mapboxgl.Popup({
          offset: 25,
          closeButton: false,
        });
        const box = document.createElement('div');
        box.style.cssText = 'padding: 8px; min-width: 150px;';
        const name = document.createElement('div');
        name.style.cssText = 'font-weight: 600; margin-bottom: 4px;';
        name.dir = 'auto';
        name.textContent = driver.full_name;
        const info = document.createElement('div');
        info.style.cssText = 'font-size: 12px; color: #666;';
        if (driver.speed) {
          const speed = document.createElement('div');
          speed.textContent = `Speed: ${driver.speed.toFixed(0)} km/h`;
          info.appendChild(speed);
        }
        const updated = document.createElement('div');
        updated.style.cssText = 'margin-top: 4px; font-size: 10px; color: #999;';
        updated.textContent = `Updated: ${new Date(driver.recorded_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baghdad' })}`;
        info.appendChild(updated);
        if (driver.source === 'tap') {
          const tap = document.createElement('div');
          tap.style.cssText = 'font-size: 10px; color: #999;';
          tap.textContent = 'From last task, not live GPS';
          info.appendChild(tap);
        }
        box.append(name, info);
        popup.setDOMContent(box);

        marker = new mapboxgl.Marker(el)
          .setLngLat([driver.lng, driver.lat])
          .setPopup(popup)
          .addTo(map.current!);

        markers.current.set(driver.driver_uid, marker);
      }
    });

    // Fit bounds if we have valid drivers
    if (validDrivers.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      validDrivers.forEach((driver) => {
        bounds.extend([driver.lng, driver.lat]);
      });

      // Fit only when the set of drivers changes, so a 30 s refresh does not undo the dispatcher's pan/zoom
      const key = validDrivers.map((d) => d.driver_uid).sort().join(',');
      // A selection moved the camera to his path; forget the old fit so closing it re-fits all drivers
      if (selectedDriverUid) fittedKey.current = '';
      else if (key !== fittedKey.current) {
        fittedKey.current = key;
        map.current.fitBounds(bounds, { padding: 50, maxZoom: 14 });
      }
    }
  }, [drivers, selectedDriverUid, mapLoaded, onDriverSelect]);

  // Draw the selected driver's path, stops, and the dashed line from the van through the stops still to do
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const m = map.current;
    if (!selectedDriverUid) {
      for (const id of ['sel-path', 'sel-next', 'sel-stops']) setSource(m, id, EMPTY);
      return;
    }

    const lines = pathLines(trail ?? []);
    setSource(m, 'sel-path', {
      type: 'FeatureCollection',
      features: lines.length ? [{ type: 'Feature', properties: {}, geometry: { type: 'MultiLineString', coordinates: lines } }] : [],
    });

    const here = selectedDriver && isValidCoordinate(selectedDriver.lat, selectedDriver.lng)
      ? [[selectedDriver.lng, selectedDriver.lat] as [number, number]]
      : [];
    const ahead = [...here, ...openStops.map((s) => [s.lng, s.lat] as [number, number])];
    setSource(m, 'sel-next', {
      type: 'FeatureCollection',
      features: ahead.length > 1 ? [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: ahead } }] : [],
    });

    setSource(m, 'sel-stops', {
      type: 'FeatureCollection',
      features: stops.map((s) => ({
        type: 'Feature',
        properties: { status: s.status, done: isDone(s), label: String(openStops.indexOf(s) + 1) },
        geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
      })),
    });
  }, [selectedDriverUid, selectedDriver, trail, stops, openStops, mapLoaded]);

  // On selection: fly to the driver at once, then fit his path and stops when they arrive. Refreshes never move the camera.
  useEffect(() => {
    if (!map.current || !mapLoaded || !selectedDriverUid) {
      selectedFitKey.current = '';
      return;
    }
    const key = `${selectedDriverUid}:${(trail || trailError) && (routes || routesError) ? 'all' : 'driver'}`;
    if (key === selectedFitKey.current) return;

    const bounds = new mapboxgl.LngLatBounds();
    if (selectedDriver && isValidCoordinate(selectedDriver.lat, selectedDriver.lng)) bounds.extend([selectedDriver.lng, selectedDriver.lat]);
    pathLines(trail ?? []).flat().forEach((p) => bounds.extend(p));
    openStops.forEach((s) => bounds.extend([s.lng, s.lat]));
    if (bounds.isEmpty()) return;

    selectedFitKey.current = key;
    map.current.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 1000 });
  }, [selectedDriverUid, selectedDriver, trail, routes, trailError, routesError, openStops, mapLoaded]);

  if (!mapboxgl.accessToken) {
    return (
      <div
        className={cn('bg-gray-100 rounded-lg flex items-center justify-center', className)}
        style={{ minHeight: '400px' }}
      >
        <div className="text-center text-gray-500">
          <p className="font-medium">Map not available</p>
          <p className="text-sm">Set VITE_MAPBOX_TOKEN to enable map view</p>
        </div>
      </div>
    );
  }

  const nextStop = openStops[0];

  return (
    <div className="relative h-full">
      {/* h-full: mapbox's CSS forces .mapboxgl-map to position:relative, so an `absolute inset-0` className alone left the map at its 400px minimum */}
      <div
        ref={mapContainer}
        className={cn('rounded-lg overflow-hidden h-full', className)}
        style={{ minHeight: '400px' }}
      />

      {/* Selected driver: path and stops summary */}
      {selectedDriverUid && (
        <div className="absolute top-4 left-4 w-72 bg-white rounded-lg shadow-lg p-3 text-sm">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-gray-900" dir="auto">{selectedDriver?.full_name ?? 'Driver'}</p>
            {onClearSelection && (
              <button
                type="button"
                onClick={onClearSelection}
                aria-label="Close driver path"
                className="text-gray-400 hover:text-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-600 mt-1">
            {trail === undefined
              ? trailError
                ? <span className="text-red-600">Could not load path</span>
                : 'Loading path…'
              : trail.length === 0
                ? 'No GPS from the driver app today'
                : `Path today since ${formatTimeBaghdad(trail[0].recorded_at)} · ${trail.length} GPS points`}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">
            {routes === undefined
              ? routesError
                ? <span className="text-red-600">Could not load stops</span>
                : 'Loading stops…'
              : stops.length === 0
                ? 'No route today'
                : `${stops.length - openStops.length} of ${stops.length} stops done`}
          </p>
          {nextStop && (
            <p className="text-xs text-gray-900 mt-1" dir="auto">
              Next: {nextStop.merchant_name || nextStop.address || `stop ${nextStop.sequence}`}
            </p>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3">
        <p className="text-xs font-medium text-gray-700 mb-2">Position Age</p>
        <div className="space-y-1">
          {STATUS_LEGEND.map(({ label, color }) => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-gray-600">{label}</span>
            </div>
          ))}
        </div>
        {selectedDriverUid && (
          <div className="space-y-1 mt-2 pt-2 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 rounded bg-blue-600" />
              <span className="text-xs text-gray-600">Path today</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 border-t-2 border-dashed border-gray-700" />
              <span className="text-xs text-gray-600">Next stops (straight line)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-400" />
              <span className="text-xs text-gray-600">Stop done</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-xs text-gray-600">Stop failed</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
